import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { CapacitorUpdater, type BundleInfo } from '@capgo/capacitor-updater';
import { useConvexClient } from 'convex-vue';
import { api } from '../../convex/_generated/api';

const PLATFORM = 'ios';
// Never swap the bundle out from under a live session — a reload lands on the
// app's start route, which drops the user out of whatever room or game they
// were in. Defer to the next safe background instead. Matches router.ts.
const LIVE_SESSION_ROUTES = new Set(['piece-game', 'classroom-game', 'classroom-lobby']);

// Module-level so App.vue's overlay reads the same state the listeners update.
const mustUpdate = ref(false);
const storeUrl = ref('');

// Self-hosted OTA + native-version gate, driven by Convex. Native-only; on
// web/PWA the service worker (VitePWA autoUpdate) handles updates. Call once
// from App.vue setup.
export function useAppUpdate() {
  if (!Capacitor.isNativePlatform()) {
    return { mustUpdate, storeUrl };
  }

  const client = useConvexClient();
  const router = useRouter();

  // A downloaded-but-not-yet-applied bundle, held until the app next backgrounds
  // on a non-game screen. `downloadingVersion` guards against re-downloading the
  // same version while a fetch is in flight.
  let pending: BundleInfo | null = null;
  let downloadingVersion: string | null = null;

  async function downloadBundle(version: string, url: string) {
    downloadingVersion = version;
    try {
      pending = await CapacitorUpdater.download({ version, url });
    } catch (err) {
      console.error('[Update] Bundle download failed:', err);
    } finally {
      downloadingVersion = null;
    }
  }

  async function applyIfSafe() {
    if (!pending) return;
    if (LIVE_SESSION_ROUTES.has(router.currentRoute.value.name as string)) return;
    const bundle = pending;
    pending = null;
    // Reload happens off-screen while backgrounded; cover it with the splash so
    // returning to the foreground lands on the new bundle with no flash.
    await SplashScreen.show();
    try {
      await CapacitorUpdater.set({ id: bundle.id });
    } catch (err) {
      console.error('[Update] Failed to apply bundle:', err);
      pending = bundle; // retry on the next safe background
      await SplashScreen.hide();
    }
  }

  // On cold start the plugin loads the bundle that was current when the app was
  // last killed. A bundle downloaded in a prior session but never applied — app
  // quit before a safe background, or only ever backgrounded mid-game — is left
  // in list() unactivated. Apply the newest such bundle here, covered by the
  // splash, so it lands now instead of waiting for another background cycle.
  async function applyDownloadedOnStartup(): Promise<boolean> {
    try {
      const [{ bundle: current }, { bundles }] = await Promise.all([
        CapacitorUpdater.current(),
        CapacitorUpdater.list(),
      ]);
      const downloadedAt = (b: BundleInfo) => Date.parse(b.downloaded) || 0;
      // We only download bundles Convex reports as newer than the running one, so
      // the most recently downloaded success bundle is the newest; the timestamp
      // guard then keeps us from ever moving backwards onto an older one.
      const next = bundles
        .filter((b) => b.status === 'success' && b.id !== current.id && b.id !== 'builtin')
        .sort((a, b) => downloadedAt(b) - downloadedAt(a))[0];
      if (!next || downloadedAt(next) <= downloadedAt(current)) return false;

      // No session can be in progress on a cold start, so applyIfSafe()'s
      // LIVE_SESSION_ROUTES guard isn't needed. set() covers the reload with the
      // splash and never resolves — the JS context is destroyed, so nothing
      // below runs on success.
      await SplashScreen.show();
      await CapacitorUpdater.set({ id: next.id });
      return true;
    } catch (err) {
      // Covers the lookup and set(). If set() failed on a bad bundle we'd already
      // shown the splash, so hide it — agent.md requires a hide on every launch
      // path or the app hangs on the splash. Hiding when we never showed is harmless.
      console.error('[Update] Startup apply failed:', err);
      await SplashScreen.hide();
      return false;
    }
  }

  async function init() {
    // Confirm the running bundle loaded OK; without this the plugin rolls a
    // freshly-applied OTA bundle back to the previous one.
    await CapacitorUpdater.notifyAppReady();

    // Ahead of the live subscription: it would otherwise re-download the
    // already-on-disk version and defer it to the next background. On a hit, set()
    // reloads into the new bundle and init() re-runs from the top there (this
    // check no-ops the second time) and wires up the subscription — doing it in
    // this soon-to-be-destroyed context would be unreliable.
    if (await applyDownloadedOnStartup()) return;

    const { version: nativeVersion } = await App.getInfo();
    const queryArgs = {
      platform: PLATFORM,
      nativeVersion,
      bundleVersion: __APP_BUNDLE_VERSION__,
    };

    // The hard native block is evaluated only at lifecycle boundaries (cold start
    // and resume), never reactively — so it can never slam down mid-session. On
    // resume we re-query fresh rather than read the subscription's cached value,
    // which is stale at that instant: the socket reconnects a beat after
    // foreground, so a floor raised while backgrounded would otherwise be missed
    // until the following resume.
    async function refreshUpdateGate() {
      try {
        const result = await client.query(api.appConfig.checkAppUpdate, queryArgs);
        storeUrl.value = result.storeUrl;
        mustUpdate.value = result.updateRequired;
      } catch {
        // Unauthorized/offline — leave the gate as-is until the next boundary.
      }
    }

    // The subscription stays live for OTA, but commits the block only on its first
    // result — the auth-aware cold-start evaluation. Later pushes drive OTA and
    // storeUrl only, leaving the visible gate to the boundary checks.
    let gateInitialized = false;
    client.onUpdate(
      api.appConfig.checkAppUpdate,
      queryArgs,
      (result) => {
        storeUrl.value = result.storeUrl;
        if (!gateInitialized) {
          mustUpdate.value = result.updateRequired;
          gateInitialized = true;
        }

        const nb = result.newBundle;
        if (
          nb &&
          !result.updateRequired && // a required native update takes priority
          nb.version !== pending?.version && // already downloaded, awaiting apply
          nb.version !== downloadingVersion // download already in flight
        ) {
          downloadBundle(nb.version, nb.url);
        }
      },
      // Before sign-in the query is Unauthorized; ignore and let Convex re-run
      // the subscription once auth lands.
      () => {},
    );

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refreshUpdateGate();
      else applyIfSafe();
    });
  }

  init();

  return { mustUpdate, storeUrl };
}
